import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { ArrowLeft, CreditCard, Database, MessageCircle } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useApi from '../api';
import { formatDate } from '../utils/dateFormatter';
import { calculateCostUsd } from './modelPricing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import type { CreditPlan } from './CreditPlansSection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LoadingState } from '@/components/ui/LoadingState';

interface StripeConnectedAccountSummary {
  stripe_account_id: string;
  status: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  disconnected_at: string | null;
}

interface OrgBillingData {
  license_type: string | null;
  identification_enabled: boolean;
  services_commerce_enabled: boolean;
  stripe_connected_account: StripeConnectedAccountSummary | null;
  monthly_credit_allotment: number | null;
  credit_plan: CreditPlan | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  billing_cycle_anchor: string | null;
  is_legacy_plan: boolean;
  cancel_at: string | null;
  enforcement_exempt: boolean;
  balance: {
    enforced: boolean;
    exhausted: boolean;
    granted: number;
    remaining: number;
    overdraft: number;
    reason: string;
  } | null;
}

interface MovePlanResult {
  change_kind: 'upgrade' | 'downgrade' | 'same';
  allotment: number;
  delta_minted: number;
}

const formatPlanPrice = (cents: number) =>
  (cents / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });

const errorDetail = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object' && 'detail' in error) {
    const detail = (error as { detail?: unknown }).detail;
    if (typeof detail === 'string') return detail;
  }
  return fallback;
};

const DEFAULT_MODEL_SELECT_VALUE = '__default__';
const REASONING_EFFORT_LABELS: Record<string, string> = {
  none: 'None',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
};

// One entry per deployment (conversations.channel). Gateway and email fall
// back to the website override server-side when left on default.
const AGENT_MODEL_DEPLOYMENTS = [
  { key: 'website', label: 'Website agent model' },
  { key: 'app', label: 'App agent model' },
  { key: 'agent_gateway', label: 'Agent gateway model' },
  { key: 'email', label: 'Email agent model' },
] as const;

const timeRangeOptions = [
  { value: '7d', label: 'Last Week' },
  { value: '30d', label: 'Last Month' },
  { value: '90d', label: 'Last Quarter' },
  { value: '365d', label: 'Last Year' },
];

interface OrganizationUser {
  id: string;
  email: string;
  name: string;
  role?: string;
}

interface Organization {
  id: string;
  name: string;
  users?: OrganizationUser[];
  license_type: string;
  trial_end_date: string | null;
  settings: any;
  operator: boolean;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  billing_cycle_anchor?: string | null;
  updated_at?: string | null;
}

interface OrganizationAnalytics {
  summary: {
    conversation_count: number;
    llm_conversation_count: number;
    total_tokens: number;
    input_tokens: number;
    output_tokens: number;
  };
  tokens_by_period_model: {
    period: string;
    model: string;
    total_tokens: number;
    input_tokens: number;
    output_tokens: number;
    conversation_count: number;
  }[];
  billing_period_llm_cost?: LlmCostSummary;
}

interface LlmCostSummary {
  period_start: string;
  period_end: string;
  total_cost_usd: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  // Prompt-cache reuse and client-side history bounding
  // (CONTEXT_MANAGEMENT_PLAN.md §4.7).
  cached_input_tokens?: number;
  cache_hit_rate?: number | null;
  context_summary_count?: number;
  by_model: {
    model: string;
    total_tokens: number;
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
  }[];
  daily: {
    date: string;
    total_cost_usd: number;
    total_tokens: number;
    sources: {
      messages: LlmUsageSource;
      agent_runs: LlmUsageSource;
    };
  }[];
}

interface LlmUsageSource {
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

interface UsageSummaryData {
  summary: {
    request_count: number;
    failure_count: number;
    failure_rate: number;
    request_units: number;
    unique_credentials: number;
    avg_duration_ms: number;
    max_duration_ms: number;
  };
  trend: {
    period: string;
    surface: string;
    request_count: number;
    failure_count: number;
  }[];
  top_operations: {
    operation: string;
    surface: string;
    request_count: number;
    failure_count: number;
  }[];
}

interface UsageEventItem {
  id: string;
  surface: string;
  operation: string;
  status: string;
  status_code: number | null;
  duration_ms: number | null;
  created_at: string | null;
}

interface UsageEventsData {
  items: UsageEventItem[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

interface StripeSummaryData {
  stripe: {
    stripe_customer_id: string | null;
    stripe_subscription_id: string | null;
    status: string | null;
    current_period_start: string | null;
    current_period_end: string | null;
    billing_cycle_anchor: string | null;
    monthly_recurring_revenue_usd: number | null;
    currency: string;
    error: string | null;
  };
  llm_cost: LlmCostSummary;
  margin: {
    monthly_recurring_revenue_usd: number | null;
    billing_period_llm_cost_usd: number;
    estimated_gross_margin_usd: number | null;
    estimated_gross_margin_percent: number | null;
    label: string;
  };
}

type IdentificationProvider = 'rb2b' | 'vector' | 'waterfall';

const toIdentificationProvider = (value: unknown): IdentificationProvider =>
  value === 'vector' || value === 'waterfall' ? value : 'rb2b';

const formatUsd = (value: number) =>
  `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatPercent = (value: number | null | undefined) =>
  typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : '-';

const formatPeriod = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const OrganizationDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, put, post, remove } = useApi();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rb2bConfigLoading, setRb2bConfigLoading] = useState(false);
  const [rb2bDomainsUpdating, setRb2bDomainsUpdating] = useState(false);
  const [rb2bEnabled, setRb2bEnabled] = useState(false);
  const [rb2bIdentifyAll, setRb2bIdentifyAll] = useState(false);
  const [identificationProvider, setIdentificationProvider] = useState<IdentificationProvider>('rb2b');
  const [rb2bIdentifyWarm, setRb2bIdentifyWarm] = useState(false);
  const [rb2bDomains, setRb2bDomains] = useState<string[]>([]);
  const [rb2bDomainInput, setRb2bDomainInput] = useState('');
  const [rb2bResolutionLimit, setRb2bResolutionLimit] = useState('');
  const [rb2bCreditBudget, setRb2bCreditBudget] = useState('');
  const [agentModelConfigLoading, setAgentModelConfigLoading] = useState(false);
  const [availableAgentModels, setAvailableAgentModels] = useState<string[]>([]);
  const [agentModels, setAgentModels] = useState<Record<string, string>>({});
  const [availableReasoningEfforts, setAvailableReasoningEfforts] = useState<string[]>([]);
  const [agentReasoningEfforts, setAgentReasoningEfforts] = useState<Record<string, string>>({});
  const [timeRange, setTimeRange] = useState('30d');
  const [analytics, setAnalytics] = useState<OrganizationAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [usageSummary, setUsageSummary] = useState<UsageSummaryData | null>(null);
  const [usageEvents, setUsageEvents] = useState<UsageEventsData | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [stripeSummary, setStripeSummary] = useState<StripeSummaryData | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeSaving, setStripeSaving] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState('');
  const [billingCycleAnchor, setBillingCycleAnchor] = useState('');
  const [accountResearchLoading, setAccountResearchLoading] = useState(false);
  const [accountResearchEnabled, setAccountResearchEnabled] = useState(false);
  const [accountResearchThreshold, setAccountResearchThreshold] = useState('');
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesEnabled, setServicesEnabled] = useState(false);
  const [allotmentLoading, setAllotmentLoading] = useState(false);
  const [allotmentSaving, setAllotmentSaving] = useState(false);
  const [allotmentValue, setAllotmentValue] = useState('');
  const [recentGrants, setRecentGrants] = useState<any[]>([]);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantSource, setGrantSource] = useState('promo');
  const [grantExpiresDays, setGrantExpiresDays] = useState('');
  const [grantIssuing, setGrantIssuing] = useState(false);
  const [billing, setBilling] = useState<OrgBillingData | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [identificationSaving, setIdentificationSaving] = useState(false);
  const [exemptSaving, setExemptSaving] = useState(false);
  const [commerceSaving, setCommerceSaving] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<CreditPlan[]>([]);
  const [movePlanId, setMovePlanId] = useState('');
  const [moveArmed, setMoveArmed] = useState(false);
  const [moving, setMoving] = useState(false);

  const fetchOrganization = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await get(`/admin/organizations/${id}`);
      setOrganization(response.data);
      setStripeCustomerId(response.data?.stripe_customer_id || '');
      setStripeSubscriptionId(response.data?.stripe_subscription_id || '');
      setBillingCycleAnchor(response.data?.billing_cycle_anchor || '');
    } catch (error) {
      console.error('Error loading organization:', error);
      toast.error('Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const loadRb2bConfig = async () => {
    if (!id) return;
    setRb2bConfigLoading(true);
    try {
      const response = await get(`/admin/organizations/${id}/rb2b-oem`);
      const payload = response.data || {};
      setRb2bEnabled(Boolean(payload.enabled));
      setRb2bIdentifyAll(Boolean(payload.identify_all_visitors));
      setIdentificationProvider(toIdentificationProvider(payload.provider));
      setRb2bIdentifyWarm(Boolean(payload.identify_warm_visitors));
      setRb2bDomains(Array.isArray(payload.domains) ? payload.domains : []);
      setRb2bResolutionLimit(
        typeof payload.resolution_limit === 'number'
          ? String(payload.resolution_limit)
          : ''
      );
      setRb2bCreditBudget(
        typeof payload.credit_budget === 'number' ? String(payload.credit_budget) : ''
      );
    } catch (error) {
      console.error('Error loading RB2B config:', error);
      toast.error('Failed to load RB2B OEM settings');
    } finally {
      setRb2bConfigLoading(false);
    }
  };

  const loadAgentModelConfig = async () => {
    if (!id) return;
    setAgentModelConfigLoading(true);
    try {
      const response = await get(`/admin/organizations/${id}/agent-models`);
      const payload = response.data || {};
      setAvailableAgentModels(Array.isArray(payload.available_models) ? payload.available_models : []);
      const nextModels: Record<string, string> = {};
      const nextEfforts: Record<string, string> = {};
      for (const { key } of AGENT_MODEL_DEPLOYMENTS) {
        const value = payload[`${key}_model`];
        nextModels[key] = typeof value === 'string' ? value : '';
        const effort = payload[`${key}_reasoning_effort`];
        nextEfforts[key] = typeof effort === 'string' ? effort : '';
      }
      setAgentModels(nextModels);
      setAvailableReasoningEfforts(
        Array.isArray(payload.available_reasoning_efforts) ? payload.available_reasoning_efforts : []
      );
      setAgentReasoningEfforts(nextEfforts);
    } catch (error) {
      console.error('Error loading agent model config:', error);
      toast.error('Failed to load agent model settings');
    } finally {
      setAgentModelConfigLoading(false);
    }
  };

  const loadAccountResearchConfig = async () => {
    if (!id) return;
    setAccountResearchLoading(true);
    try {
      const response = await get(`/admin/organizations/${id}/account-research`);
      const payload = response.data || {};
      setAccountResearchEnabled(Boolean(payload.enabled));
      setAccountResearchThreshold(
        typeof payload.notify_threshold === 'number'
          ? String(payload.notify_threshold)
          : ''
      );
    } catch (error) {
      console.error('Error loading account research config:', error);
      toast.error('Failed to load account research settings');
    } finally {
      setAccountResearchLoading(false);
    }
  };

  const loadServicesConfig = async () => {
    if (!id) return;
    setServicesLoading(true);
    try {
      const response = await get(`/admin/organizations/${id}/services`);
      const payload = response.data || {};
      setServicesEnabled(Boolean(payload.enabled));
    } catch (error) {
      console.error('Error loading services config:', error);
      toast.error('Failed to load services settings');
    } finally {
      setServicesLoading(false);
    }
  };

  const loadAllotment = async () => {
    if (!id) return;
    setAllotmentLoading(true);
    try {
      const response = await get(`/admin/metering/organizations/${id}/allotment`);
      const value = response.data?.monthly_credit_allotment;
      setAllotmentValue(typeof value === 'number' ? String(value) : '');
      setRecentGrants(Array.isArray(response.data?.recent_grants) ? response.data.recent_grants : []);
    } catch (error) {
      console.error('Error loading credit allotment:', error);
    } finally {
      setAllotmentLoading(false);
    }
  };

  const handleSaveAllotment = async () => {
    if (!id) return;
    const trimmed = allotmentValue.trim();
    let payload: number | null = null;
    if (trimmed !== '') {
      const parsed = Number.parseInt(trimmed, 10);
      if (!Number.isFinite(parsed) || parsed < 1 || parsed > 10_000_000) {
        toast.error('Monthly credit allotment must be between 1 and 10,000,000 (or blank to clear)');
        return;
      }
      payload = parsed;
    }
    setAllotmentSaving(true);
    try {
      await put(`/admin/metering/organizations/${id}/allotment`, {
        monthly_credit_allotment: payload,
      });
      toast.success('Credit allotment updated');
      loadAllotment();
    } catch (error) {
      console.error('Error saving credit allotment:', error);
      toast.error('Failed to save credit allotment');
    } finally {
      setAllotmentSaving(false);
    }
  };

  const handleIssueGrant = async () => {
    if (!id) return;
    const amount = Number.parseInt(grantAmount.trim(), 10);
    if (!Number.isFinite(amount) || amount < 1 || amount > 10_000_000) {
      toast.error('Grant amount must be between 1 and 10,000,000 credits');
      return;
    }
    let expiresDays: number | null = null;
    if (grantExpiresDays.trim() !== '') {
      expiresDays = Number.parseInt(grantExpiresDays.trim(), 10);
      if (!Number.isFinite(expiresDays) || expiresDays < 1 || expiresDays > 3650) {
        toast.error('Expiry must be between 1 and 3650 days (or blank for never)');
        return;
      }
    }
    setGrantIssuing(true);
    try {
      await post(`/admin/metering/organizations/${id}/grants`, {
        amount,
        source: grantSource,
        expires_in_days: expiresDays,
      });
      toast.success(`Issued ${amount.toLocaleString()} credits (${grantSource})`);
      setGrantAmount('');
      setGrantExpiresDays('');
      loadAllotment();
    } catch (error: any) {
      console.error('Error issuing grant:', error);
      toast.error(typeof error?.detail === 'string' ? error.detail : 'Failed to issue grant');
    } finally {
      setGrantIssuing(false);
    }
  };

  const loadBilling = async () => {
    if (!id) return;
    setBillingLoading(true);
    try {
      const [billingResponse, plansResponse] = await Promise.all([
        get(`/admin/metering/organizations/${id}/billing`),
        get('/admin/metering/plans'),
      ]);
      setBilling(billingResponse.data as OrgBillingData);
      const plans: CreditPlan[] = Array.isArray(plansResponse.data?.plans) ? plansResponse.data.plans : [];
      setAvailablePlans(
        plans
          .filter((plan) => plan.kind === 'subscription' && plan.is_active && !!plan.stripe_price_id)
          .sort((a, b) => a.sort_order - b.sort_order || a.price_cents - b.price_cents)
      );
    } catch (error) {
      console.error('Error loading org billing:', error);
      toast.error('Failed to load billing/plan details');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleToggleIdentification = async (enabled: boolean) => {
    if (!id || !billing) return;
    const previous = billing.identification_enabled;
    setBilling({ ...billing, identification_enabled: enabled });
    setIdentificationSaving(true);
    try {
      await put(`/admin/metering/organizations/${id}/identification`, { enabled });
      toast.success(enabled ? 'Visitor identification enabled' : 'Visitor identification disabled');
    } catch (error) {
      console.error('Error updating identification capability:', error);
      setBilling((current) => (current ? { ...current, identification_enabled: previous } : current));
      toast.error(errorDetail(error, 'Failed to update identification capability'));
    } finally {
      setIdentificationSaving(false);
    }
  };

  const handleToggleEnforcementExempt = async (exempt: boolean) => {
    if (!id || !billing) return;
    const previous = billing.enforcement_exempt;
    setBilling({ ...billing, enforcement_exempt: exempt });
    setExemptSaving(true);
    try {
      await put(`/admin/metering/organizations/${id}/enforcement-exempt`, { exempt });
      toast.success(exempt ? 'Credit enforcement bypassed for this org' : 'Credit enforcement re-enabled');
    } catch (error) {
      console.error('Error updating enforcement exemption:', error);
      setBilling((current) => (current ? { ...current, enforcement_exempt: previous } : current));
      toast.error(errorDetail(error, 'Failed to update enforcement exemption'));
    } finally {
      setExemptSaving(false);
    }
  };

  const handleReconnectStripe = async () => {
    if (!id || !billing?.stripe_connected_account) return;
    setReconnecting(true);
    try {
      const { data } = await post(`/admin/metering/organizations/${id}/stripe-connect/reconnect`, {});
      const account = data.stripe_connected_account as StripeConnectedAccountSummary;
      setBilling((current) => (current ? { ...current, stripe_connected_account: account } : current));
      toast.success(`Stripe account reconnected (${account.status})`);
    } catch (error) {
      console.error('Error reconnecting Stripe account:', error);
      toast.error(errorDetail(error, 'Failed to reconnect Stripe account'));
    } finally {
      setReconnecting(false);
    }
  };

  const handleToggleCommerce = async (enabled: boolean) => {
    if (!id || !billing) return;
    const previous = billing.services_commerce_enabled;
    setBilling({ ...billing, services_commerce_enabled: enabled });
    setCommerceSaving(true);
    try {
      await put(`/admin/metering/organizations/${id}/services-commerce`, { enabled });
      toast.success(enabled ? 'Services commerce enabled' : 'Services commerce disabled');
    } catch (error) {
      console.error('Error updating services commerce flag:', error);
      setBilling((current) => (current ? { ...current, services_commerce_enabled: previous } : current));
      toast.error(errorDetail(error, 'Failed to update services commerce flag'));
    } finally {
      setCommerceSaving(false);
    }
  };

  const handleMovePlan = async () => {
    if (!id || !movePlanId) return;
    if (!moveArmed) {
      setMoveArmed(true);
      return;
    }
    setMoving(true);
    try {
      const response = await post(`/admin/metering/organizations/${id}/move-plan`, { plan_id: movePlanId });
      const result = response.data as MovePlanResult;
      const delta =
        result.delta_minted > 0 ? `, +${Number(result.delta_minted).toLocaleString()} credits this cycle` : '';
      toast.success(
        `Moved (${result.change_kind}): allotment ${Number(result.allotment).toLocaleString()}${delta}`
      );
      setMovePlanId('');
      setMoveArmed(false);
      loadBilling();
      loadAllotment();
      fetchOrganization();
    } catch (error) {
      console.error('Error moving org plan:', error);
      toast.error(errorDetail(error, 'Failed to move plan'));
      setMoveArmed(false);
    } finally {
      setMoving(false);
    }
  };

  const fetchAnalytics = async () => {
    if (!id) return;
    setAnalyticsLoading(true);
    try {
      const response = await get(`/admin/organizations/${id}/analytics?time_range=${timeRange}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading organization analytics:', error);
      toast.error('Failed to load organization analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchUsage = async () => {
    if (!id) return;
    setUsageLoading(true);
    try {
      const summaryResponse = await get(`/admin/organizations/${id}/usage?time_range=${timeRange}`);
      setUsageSummary(summaryResponse.data);
      const eventsResponse = await get(`/admin/usage/events?organization_id=${id}&page=1&per_page=10`);
      setUsageEvents(eventsResponse.data);
    } catch (error) {
      console.error('Error loading organization usage:', error);
      toast.error('Failed to load API/MCP usage');
    } finally {
      setUsageLoading(false);
    }
  };

  const fetchStripeSummary = async () => {
    if (!id) return;
    setStripeLoading(true);
    try {
      const response = await get(`/admin/organizations/${id}/stripe-summary`);
      setStripeSummary(response.data);
    } catch (error) {
      console.error('Error loading Stripe summary:', error);
      toast.error('Failed to load Stripe summary');
    } finally {
      setStripeLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganization();
    loadRb2bConfig();
    loadAgentModelConfig();
    loadAccountResearchConfig();
    loadServicesConfig();
    loadAllotment();
    loadBilling();
    fetchStripeSummary();
  }, [id]);

  useEffect(() => {
    fetchAnalytics();
    fetchUsage();
  }, [id, timeRange]);

  const updateOrganizationField = (name: keyof Organization, value: any) => {
    if (!organization) return;
    setOrganization({ ...organization, [name]: value });
  };

  const handleSave = async () => {
    if (!id || !organization) return;
    setSaving(true);
    try {
      await put(`/admin/organizations/${id}`, {
        name: organization.name,
        license_type: organization.license_type,
        trial_end_date: organization.trial_end_date ? new Date(organization.trial_end_date).toISOString() : null,
      });

      const trimmedLimit = rb2bResolutionLimit.trim();
      let resolutionLimitPayload: number | null = null;
      if (trimmedLimit !== '') {
        const parsed = Number.parseInt(trimmedLimit, 10);
        if (!Number.isFinite(parsed) || parsed < 0) {
          toast.error('Resolution limit must be a non-negative integer');
          return;
        }
        resolutionLimitPayload = parsed;
      }

      const trimmedBudget = rb2bCreditBudget.trim();
      let creditBudgetPayload: number | null = null;
      if (trimmedBudget !== '') {
        const parsed = Number.parseInt(trimmedBudget, 10);
        if (!Number.isFinite(parsed) || parsed < 0) {
          toast.error('Credit budget must be a non-negative integer');
          return;
        }
        creditBudgetPayload = parsed;
      }

      await put(`/admin/organizations/${id}/rb2b-oem`, {
        enabled: rb2bEnabled,
        provider: identificationProvider,
        identify_all_visitors: rb2bIdentifyAll,
        identify_warm_visitors: rb2bIdentifyWarm,
        resolution_limit: resolutionLimitPayload,
        credit_budget: creditBudgetPayload,
      });
      await put(`/admin/organizations/${id}/agent-models`, {
        ...Object.fromEntries(
          AGENT_MODEL_DEPLOYMENTS.map(({ key }) => [
            `${key}_model`,
            (agentModels[key] || '').trim() || null,
          ])
        ),
        ...Object.fromEntries(
          AGENT_MODEL_DEPLOYMENTS.map(({ key }) => [
            `${key}_reasoning_effort`,
            (agentReasoningEfforts[key] || '').trim() || null,
          ])
        ),
      });

      const trimmedThreshold = accountResearchThreshold.trim();
      let thresholdPayload: number | undefined;
      if (trimmedThreshold !== '') {
        const parsedThreshold = Number.parseInt(trimmedThreshold, 10);
        if (!Number.isFinite(parsedThreshold) || parsedThreshold < 1 || parsedThreshold > 100) {
          toast.error('Fit notification threshold must be between 1 and 100');
          return;
        }
        thresholdPayload = parsedThreshold;
      }
      await put(`/admin/organizations/${id}/account-research`, {
        enabled: accountResearchEnabled,
        ...(thresholdPayload !== undefined ? { notify_threshold: thresholdPayload } : {}),
      });

      await put(`/admin/organizations/${id}/services`, {
        enabled: servicesEnabled,
      });
      toast.success('Organization updated successfully');
      fetchOrganization();
      loadServicesConfig();
    } catch (error) {
      console.error('Error saving organization:', error);
      toast.error('Failed to save organization');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStripe = async () => {
    if (!id) return;
    setStripeSaving(true);
    try {
      await put(`/admin/organizations/${id}/stripe`, {
        stripe_customer_id: stripeCustomerId.trim() || null,
        stripe_subscription_id: stripeSubscriptionId.trim() || null,
        billing_cycle_anchor: billingCycleAnchor.trim() ? new Date(billingCycleAnchor.trim()).toISOString() : null,
      });
      toast.success('Stripe fields updated');
      await fetchOrganization();
      await fetchStripeSummary();
    } catch (error) {
      console.error('Error saving Stripe fields:', error);
      toast.error('Failed to save Stripe fields');
    } finally {
      setStripeSaving(false);
    }
  };

  const handleAddRb2bDomain = async () => {
    if (!id) return;
    const nextDomain = rb2bDomainInput.trim();
    if (!nextDomain) return;

    setRb2bDomainsUpdating(true);
    try {
      const response = await post(`/admin/organizations/${id}/rb2b-oem/domains`, { domain: nextDomain });
      const payload = response.data || {};
      setRb2bDomains(Array.isArray(payload.domains) ? payload.domains : []);
      setRb2bDomainInput('');
      toast.success('RB2B domain added');
    } catch (error: any) {
      toast.error(typeof error?.detail === 'string' ? error.detail : 'Failed to add RB2B domain');
    } finally {
      setRb2bDomainsUpdating(false);
    }
  };

  const handleRemoveRb2bDomain = async (domain: string) => {
    if (!id) return;
    setRb2bDomainsUpdating(true);
    try {
      const response = await remove(`/admin/organizations/${id}/rb2b-oem/domains/${encodeURIComponent(domain)}`);
      const payload = response.data || {};
      setRb2bDomains(Array.isArray(payload.domains) ? payload.domains : []);
      toast.success('RB2B domain removed');
    } catch (error: any) {
      toast.error(typeof error?.detail === 'string' ? error.detail : 'Failed to remove RB2B domain');
    } finally {
      setRb2bDomainsUpdating(false);
    }
  };

  const agentModelOptions = (key: string) => {
    const current = agentModels[key];
    return current && !availableAgentModels.includes(current)
      ? [current, ...availableAgentModels]
      : availableAgentModels;
  };

  const analyticsSummary = useMemo(() => {
    if (!analytics) {
      return {
        totalCost: 0,
        avgCostPerConversation: 0,
      };
    }
    const totalCost = analytics.tokens_by_period_model.reduce(
      (sum, item) => sum + calculateCostUsd(item.model, item.input_tokens || 0, item.output_tokens || 0),
      0
    );
    const llmConversations = analytics.summary.llm_conversation_count || 0;
    return {
      totalCost,
      avgCostPerConversation: llmConversations > 0 ? totalCost / llmConversations : 0,
    };
  }, [analytics]);

  const costTrendData = useMemo(() => {
    if (!analytics) return [];
    const periodMap = new Map<string, { period: string; cost: number; tokens: number }>();
    for (const item of analytics.tokens_by_period_model) {
      const row = periodMap.get(item.period) || { period: item.period, cost: 0, tokens: 0 };
      row.cost += calculateCostUsd(item.model, item.input_tokens || 0, item.output_tokens || 0);
      row.tokens += item.total_tokens || 0;
      periodMap.set(item.period, row);
    }
    return Array.from(periodMap.values()).sort(
      (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
    );
  }, [analytics]);

  const usageTrendData = useMemo(() => {
    const periodMap = new Map<string, { period: string; api: number; mcp: number; failures: number }>();
    for (const item of usageSummary?.trend || []) {
      const row = periodMap.get(item.period) || { period: item.period, api: 0, mcp: 0, failures: 0 };
      if (item.surface === 'api') {
        row.api += item.request_count || 0;
      } else if (item.surface === 'mcp') {
        row.mcp += item.request_count || 0;
      }
      row.failures += item.failure_count || 0;
      periodMap.set(item.period, row);
    }
    return Array.from(periodMap.values()).sort(
      (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
    );
  }, [usageSummary]);

  const billingPeriodCost = analytics?.billing_period_llm_cost || stripeSummary?.llm_cost || null;
  const billingCostTrendData = useMemo(
    () =>
      (billingPeriodCost?.daily || []).map((day) => ({
        date: day.date,
        websiteAppCost: day.sources.messages.cost_usd,
        backgroundCost: day.sources.agent_runs.cost_usd,
        websiteAppTokens: day.sources.messages.total_tokens,
        backgroundTokens: day.sources.agent_runs.total_tokens,
      })),
    [billingPeriodCost]
  );

  if (loading && !organization) {
    return <LoadingState label="Loading organization..." className="min-h-[60vh]" />;
  }

  if (!organization) {
    return (
      <div className="px-6 py-4 w-full space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to admin
        </Button>
        <div className="text-muted-foreground">Organization not found.</div>
      </div>
    );
  }

  return (
    <div className="px-6 py-4 w-full space-y-6 overflow-y-auto">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to admin
          </Button>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold">{organization.name}</h1>
        <p className="text-xs font-mono text-muted-foreground">{organization.id}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Organization Settings</CardTitle>
              <CardDescription>Core account settings and defaults.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="organization-name">Name</Label>
                <Input
                  id="organization-name"
                  value={organization.name}
                  onChange={(event) => updateOrganizationField('name', event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="license-type">License Type</Label>
                <Input
                  id="license-type"
                  value={organization.license_type || ''}
                  onChange={(event) => updateOrganizationField('license_type', event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label>Trial End Date</Label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'flex-1 justify-start text-left font-normal',
                          !organization.trial_end_date && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {organization.trial_end_date
                          ? format(new Date(organization.trial_end_date), 'PPP')
                          : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={organization.trial_end_date ? new Date(organization.trial_end_date) : undefined}
                        onSelect={(date) => updateOrganizationField('trial_end_date', date ? date.toISOString() : null)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {organization.trial_end_date && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => updateOrganizationField('trial_end_date', null)}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Agent Models</CardTitle>
              <CardDescription>Override the default agent model and reasoning effort per deployment for this organization. Gateway and email use the website override when left on default. Default effort is model-aware (Luna runs at extra high, other models at low); efforts above what a model supports are clamped down.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              {AGENT_MODEL_DEPLOYMENTS.map(({ key, label }) => (
                <div className="grid gap-2" key={key}>
                  <Label htmlFor={`${key}-agent-model`}>{label}</Label>
                  <Select
                    value={agentModels[key] || DEFAULT_MODEL_SELECT_VALUE}
                    onValueChange={(value) =>
                      setAgentModels((prev) => ({ ...prev, [key]: value === DEFAULT_MODEL_SELECT_VALUE ? '' : value }))
                    }
                    disabled={agentModelConfigLoading}
                  >
                    <SelectTrigger id={`${key}-agent-model`}>
                      <SelectValue placeholder="Use default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DEFAULT_MODEL_SELECT_VALUE}>Use default</SelectItem>
                      {agentModelOptions(key).map((model) => (
                        <SelectItem key={model} value={model}>{model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Label htmlFor={`${key}-agent-effort`} className="text-xs text-muted-foreground">Reasoning effort</Label>
                  <Select
                    value={agentReasoningEfforts[key] || DEFAULT_MODEL_SELECT_VALUE}
                    onValueChange={(value) =>
                      setAgentReasoningEfforts((prev) => ({ ...prev, [key]: value === DEFAULT_MODEL_SELECT_VALUE ? '' : value }))
                    }
                    disabled={agentModelConfigLoading}
                  >
                    <SelectTrigger id={`${key}-agent-effort`}>
                      <SelectValue placeholder="Use default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DEFAULT_MODEL_SELECT_VALUE}>Use default</SelectItem>
                      {availableReasoningEfforts.map((effort) => (
                        <SelectItem key={effort} value={effort}>{REASONING_EFFORT_LABELS[effort] || effort}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Visitor identification</CardTitle>
              <CardDescription>Configure visitor identification for this organization.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="identification-provider">Provider</Label>
                <Select
                  value={identificationProvider}
                  onValueChange={(value) => setIdentificationProvider(toIdentificationProvider(value))}
                  disabled={rb2bConfigLoading}
                >
                  <SelectTrigger id="identification-provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rb2b">RB2B</SelectItem>
                    <SelectItem value="vector">Vector</SelectItem>
                    <SelectItem value="waterfall">Waterfall (Vector, then RB2B)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {identificationProvider === 'waterfall'
                    ? 'Both pixels run on every page and a Vector person match wins. Each provider that identifies a visitor is charged, so a visitor both identify costs two identifications.'
                    : "Which pixel identifies this organization's visitors. Limits, websites and credits apply to both."}
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={rb2bEnabled}
                  onChange={(event) => setRb2bEnabled(event.target.checked)}
                  disabled={rb2bConfigLoading}
                />
                <span>Enable for this organization</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={rb2bIdentifyAll}
                  onChange={(event) => {
                    setRb2bIdentifyAll(event.target.checked);
                    if (event.target.checked) setRb2bIdentifyWarm(false);
                  }}
                  disabled={rb2bConfigLoading || !rb2bEnabled}
                />
                <span>Identify all visitors</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={rb2bIdentifyWarm}
                  onChange={(event) => {
                    setRb2bIdentifyWarm(event.target.checked);
                    if (event.target.checked) setRb2bIdentifyAll(false);
                  }}
                  disabled={rb2bConfigLoading || !rb2bEnabled}
                />
                <span>Identify warm visitors only (intent-scored)</span>
              </label>
              <div className="grid gap-2">
                <Label htmlFor="rb2b-resolution-limit">Resolution limit per billing period</Label>
                <Input
                  id="rb2b-resolution-limit"
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  placeholder="Default: 1000"
                  value={rb2bResolutionLimit}
                  onChange={(event) => setRb2bResolutionLimit(event.target.value)}
                  disabled={rb2bConfigLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Plan ceiling in identified visitors. Customers can't change it.
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rb2b-credit-budget">Customer credit budget per billing period</Label>
                <Input
                  id="rb2b-credit-budget"
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  placeholder="No budget"
                  value={rb2bCreditBudget}
                  onChange={(event) => setRb2bCreditBudget(event.target.value)}
                  disabled={rb2bConfigLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Customer-set spend control, in credits. Converted at the rate card; whichever ceiling is lower applies.
                </p>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="customer.example.com"
                  value={rb2bDomainInput}
                  onChange={(event) => setRb2bDomainInput(event.target.value)}
                  disabled={rb2bConfigLoading || rb2bDomainsUpdating}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddRb2bDomain();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddRb2bDomain}
                  disabled={rb2bConfigLoading || rb2bDomainsUpdating}
                >
                  Add
                </Button>
              </div>
              <div className="space-y-1 max-h-44 overflow-y-auto border rounded-md p-2">
                {rb2bDomains.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No RB2B domains configured.</div>
                ) : (
                  rb2bDomains.map((domain) => (
                    <div key={domain} className="flex items-center justify-between gap-2">
                      <span className="text-sm font-mono">{domain}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveRb2bDomain(domain)}
                        disabled={rb2bConfigLoading || rb2bDomainsUpdating}
                      >
                        Remove
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Account Research</CardTitle>
              <CardDescription>
                Background AI research on identified visitor companies: ICP fit score, reasoning,
                and high-fit notifications.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={accountResearchEnabled}
                  onChange={(event) => setAccountResearchEnabled(event.target.checked)}
                  disabled={accountResearchLoading}
                />
                <span>Enable for this organization</span>
              </label>
              <div className="grid gap-2">
                <Label htmlFor="account-research-threshold">Fit notification threshold (1-100)</Label>
                <Input
                  id="account-research-threshold"
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  inputMode="numeric"
                  placeholder="Default: 70"
                  value={accountResearchThreshold}
                  onChange={(event) => setAccountResearchThreshold(event.target.value)}
                  disabled={accountResearchLoading || !accountResearchEnabled}
                />
                <p className="text-xs text-muted-foreground">
                  Scores at or above this fire the high-fit account notification (email + Slack).
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Services</CardTitle>
              <CardDescription>
                Customer-defined background agents. Authoring remains available when disabled,
                but manual, scheduled, and agent-triggered runs will not execute.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={servicesEnabled}
                  onChange={(event) => setServicesEnabled(event.target.checked)}
                  disabled={servicesLoading}
                />
                <span>Enable for this organization</span>
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Credits</CardTitle>
              <CardDescription>
                Monthly credit allotment — sizes the recurring included-credit grant each billing
                cycle. Edits apply from the next cycle grant onward; the current cycle&apos;s grant is
                never rewritten. Legacy defaults: pro 20,000 · business 100,000; activate is set here.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Plan + capabilities (CREDIT_PLANS_PLAN.md §4) */}
              <div className="space-y-3 rounded-md border p-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label>Plan</Label>
                    {billingLoading && !billing ? (
                      <p className="mt-1 text-sm text-muted-foreground">Loading…</p>
                    ) : billing?.credit_plan ? (
                      <p className="mt-1 text-sm">
                        <span className="font-medium">{billing.credit_plan.name}</span>
                        <span className="text-muted-foreground">
                          {' '}
                          · {formatPlanPrice(billing.credit_plan.price_cents)}
                          {billing.credit_plan.interval ? '/mo' : ''} ·{' '}
                          {billing.credit_plan.credits.toLocaleString()} credits
                        </span>
                      </p>
                    ) : billing?.is_legacy_plan ? (
                      <p className="mt-1 text-sm">
                        <Badge variant="outline">Legacy plan</Badge>{' '}
                        <span className="capitalize">{billing.license_type || 'unknown'}</span>
                        <span className="text-muted-foreground"> — allotment is hand-set below</span>
                      </p>
                    ) : (
                      <p className="mt-1 text-sm text-muted-foreground">
                        No plan{billing?.license_type ? ` (${billing.license_type})` : ''}
                      </p>
                    )}
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch
                      checked={!!billing?.identification_enabled}
                      disabled={!billing || identificationSaving}
                      onCheckedChange={handleToggleIdentification}
                    />
                    Visitor identification enabled
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch
                      checked={!!billing?.services_commerce_enabled}
                      disabled={!billing || commerceSaving}
                      onCheckedChange={handleToggleCommerce}
                    />
                    Services commerce (Stripe Connect) enabled
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch
                      checked={!!billing?.enforcement_exempt}
                      disabled={!billing || exemptSaving}
                      onCheckedChange={handleToggleEnforcementExempt}
                    />
                    Bypass credit enforcement (white-glove)
                  </label>
                  {billing?.balance && (
                    <div className="text-sm text-muted-foreground">
                      Balance: {billing.balance.remaining.toLocaleString()} remaining of{' '}
                      {billing.balance.granted.toLocaleString()}
                      {billing.balance.overdraft > 0 ? ` · ${billing.balance.overdraft.toLocaleString()} overdraft` : ''}
                      {' · '}
                      <span className={billing.balance.enforced && billing.balance.exhausted ? 'font-medium text-destructive' : ''}>
                        {billing.balance.reason}
                      </span>
                    </div>
                  )}
                  {billing?.stripe_connected_account && (
                    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                      <span className="font-mono text-xs">{billing.stripe_connected_account.stripe_account_id}</span>
                      <Badge variant={billing.stripe_connected_account.status === 'active' ? 'default' : 'secondary'}>
                        {billing.stripe_connected_account.status}
                      </Badge>
                      {billing.stripe_connected_account.disconnected_at && (
                        <Button size="sm" variant="outline" onClick={handleReconnectStripe} disabled={reconnecting}>
                          {reconnecting ? 'Reconnecting…' : 'Reconnect'}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label>Move to plan</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      value={movePlanId}
                      onValueChange={(value) => {
                        setMovePlanId(value);
                        setMoveArmed(false);
                      }}
                      disabled={!billing?.stripe_subscription_id || moving || availablePlans.length === 0}
                    >
                      <SelectTrigger className="w-72">
                        <SelectValue placeholder="Select a rung" />
                      </SelectTrigger>
                      <SelectContent>
                        {availablePlans.map((plan) => (
                          <SelectItem key={plan.id} value={plan.id} disabled={plan.id === billing?.credit_plan?.id}>
                            {plan.name} — {formatPlanPrice(plan.price_cents)}/mo
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant={moveArmed ? 'destructive' : 'outline'}
                      onClick={handleMovePlan}
                      disabled={!billing?.stripe_subscription_id || !movePlanId || moving}
                    >
                      {moving ? 'Moving…' : moveArmed ? 'Confirm move' : 'Move'}
                    </Button>
                    {moveArmed && !moving && (
                      <Button variant="ghost" size="sm" onClick={() => setMoveArmed(false)}>
                        Cancel
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {billing?.stripe_subscription_id
                      ? 'Changes the live Stripe subscription. Upgrades invoice the prorated difference now and mint the credit delta for this cycle; downgrades renew at the lower rung.'
                      : 'Customer must check out first; set an allotment manually for legacy/manual billing.'}
                  </p>
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="credit-allotment">Monthly credit allotment</Label>
                <Input
                  id="credit-allotment"
                  type="number"
                  min={1}
                  max={10000000}
                  step={1000}
                  inputMode="numeric"
                  placeholder="Blank: no allotment (usage shown without a denominator)"
                  value={allotmentValue}
                  onChange={(event) => setAllotmentValue(event.target.value)}
                  disabled={allotmentLoading}
                />
              </div>
              <Button onClick={handleSaveAllotment} disabled={allotmentSaving || allotmentLoading}>
                {allotmentSaving ? 'Saving…' : 'Save allotment'}
              </Button>

              <div className="border-t pt-4">
                <Label>Issue a one-off grant</Label>
                <p className="mb-2 mt-1 text-xs text-muted-foreground">
                  Promo credits, a migration adjustment, or a manually-recorded pack. Takes effect
                  immediately. Blank expiry = never expires (pack semantics).
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    className="w-36"
                    type="number"
                    min={1}
                    step={1000}
                    inputMode="numeric"
                    placeholder="Credits"
                    value={grantAmount}
                    onChange={(event) => setGrantAmount(event.target.value)}
                  />
                  <Select value={grantSource} onValueChange={setGrantSource}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="promo">Promo</SelectItem>
                      <SelectItem value="topup">Top-up</SelectItem>
                      <SelectItem value="migration">Migration</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    className="w-36"
                    type="number"
                    min={1}
                    max={3650}
                    inputMode="numeric"
                    placeholder="Expires (days)"
                    value={grantExpiresDays}
                    onChange={(event) => setGrantExpiresDays(event.target.value)}
                  />
                  <Button variant="outline" onClick={handleIssueGrant} disabled={grantIssuing}>
                    {grantIssuing ? 'Issuing…' : 'Issue grant'}
                  </Button>
                </div>
              </div>

              {recentGrants.length > 0 && (
                <div className="border-t pt-4">
                  <Label>Recent grants</Label>
                  <div className="mt-2 space-y-1">
                    {recentGrants.map((grant) => (
                      <div
                        key={grant.id}
                        className="flex items-center justify-between text-xs text-muted-foreground"
                      >
                        <span>
                          {grant.source} · {Number(grant.amount).toLocaleString()} credits
                          {Number(grant.consumed) > 0 &&
                            ` (${Number(grant.consumed).toLocaleString()} consumed)`}
                        </span>
                        <span className="tabular-nums">
                          {grant.granted_at ? new Date(grant.granted_at).toLocaleDateString() : ''}
                          {' — '}
                          {grant.expires_at
                            ? `expires ${new Date(grant.expires_at).toLocaleDateString()}`
                            : 'never expires'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Stripe Billing</CardTitle>
              <CardDescription>Manually backfill Stripe IDs and billing anchor for this organization.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="stripe-customer-id">Stripe customer ID</Label>
                <Input
                  id="stripe-customer-id"
                  placeholder="cus_..."
                  value={stripeCustomerId}
                  onChange={(event) => setStripeCustomerId(event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="stripe-subscription-id">Stripe subscription ID</Label>
                <Input
                  id="stripe-subscription-id"
                  placeholder="sub_..."
                  value={stripeSubscriptionId}
                  onChange={(event) => setStripeSubscriptionId(event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="billing-cycle-anchor">Billing cycle anchor</Label>
                <Input
                  id="billing-cycle-anchor"
                  placeholder="2026-06-01T00:00:00"
                  value={billingCycleAnchor}
                  onChange={(event) => setBillingCycleAnchor(event.target.value)}
                />
              </div>
              <Button type="button" variant="outline" onClick={handleSaveStripe} disabled={stripeSaving}>
                {stripeSaving ? 'Saving Stripe...' : 'Save Stripe Fields'}
              </Button>
              <div className="space-y-1 text-xs text-muted-foreground">
                <div>Status: {stripeSummary?.stripe.status || 'Unknown'}</div>
                <div>Current period: {stripeSummary?.stripe.current_period_start ? formatDate(stripeSummary.stripe.current_period_start) : '-'} to {stripeSummary?.stripe.current_period_end ? formatDate(stripeSummary.stripe.current_period_end) : '-'}</div>
                {stripeSummary?.stripe.error && <div className="text-red-600">Stripe lookup error: {stripeSummary.stripe.error}</div>}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>{organization.users?.length || 0} users in this organization.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(organization.users || []).map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell className="text-xs">{user.email}</TableCell>
                    </TableRow>
                  ))}
                  {(organization.users || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} className="h-24 text-center">
                        No users in this organization.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Billing Period Margin</CardTitle>
              <CardDescription>Estimated subscription revenue minus captured LLM cost for the current billing period.</CardDescription>
            </div>
            <CreditCard className="h-5 w-5 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stripeSummary?.margin.monthly_recurring_revenue_usd != null
                    ? formatUsd(stripeSummary.margin.monthly_recurring_revenue_usd)
                    : '-'}
                </div>
                <p className="text-xs text-muted-foreground">From Stripe subscription</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">LLM Cost</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatUsd(billingPeriodCost?.total_cost_usd || 0)}</div>
                <p className="text-xs text-muted-foreground">Current billing period</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Estimated Margin</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stripeSummary?.margin.estimated_gross_margin_usd != null
                    ? formatUsd(stripeSummary.margin.estimated_gross_margin_usd)
                    : '-'}
                </div>
                <p className="text-xs text-muted-foreground">Revenue minus LLM cost</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Margin %</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPercent(stripeSummary?.margin.estimated_gross_margin_percent)}</div>
                <p className="text-xs text-muted-foreground">LLM-only estimate</p>
              </CardContent>
            </Card>
          </div>
          {stripeLoading ? (
            <LoadingState label="Loading Stripe summary..." />
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="text-sm text-muted-foreground">
                Billing period: {billingPeriodCost?.period_start ? formatDate(billingPeriodCost.period_start) : '-'} to {billingPeriodCost?.period_end ? formatDate(billingPeriodCost.period_end) : '-'}
              </div>
              <div className="text-sm text-muted-foreground">
                Tokens: {(billingPeriodCost?.total_tokens || 0).toLocaleString()} total ({(billingPeriodCost?.input_tokens || 0).toLocaleString()} input / {(billingPeriodCost?.output_tokens || 0).toLocaleString()} output)
                {typeof billingPeriodCost?.cache_hit_rate === 'number' && (
                  <>
                    {' '}· Cache hit rate: {(billingPeriodCost.cache_hit_rate * 100).toFixed(1)}% ({(billingPeriodCost.cached_input_tokens || 0).toLocaleString()} cached input)
                  </>
                )}
                {typeof billingPeriodCost?.context_summary_count === 'number' && billingPeriodCost.context_summary_count > 0 && (
                  <>
                    {' '}· Context summaries: {billingPeriodCost.context_summary_count.toLocaleString()}
                  </>
                )}
              </div>
            </div>
          )}
          <div className="pt-2">
            <div className="mb-4">
              <h3 className="text-lg font-semibold">Daily LLM Cost</h3>
              <p className="text-sm text-muted-foreground">
                Daily estimated cost split between website/app agents and background agent runs.
              </p>
            </div>
            {billingCostTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={billingCostTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatPeriod} />
                  <YAxis tickFormatter={(value) => `$${Number(value).toFixed(2)}`} />
                  <Tooltip
                    formatter={(value) => formatUsd(Number(value || 0))}
                    labelFormatter={(value) => formatPeriod(String(value))}
                  />
                  <Legend />
                  <Bar
                    dataKey="websiteAppCost"
                    name="Website + app agents"
                    stackId="llm-cost"
                    fill="#2563eb"
                  />
                  <Bar
                    dataKey="backgroundCost"
                    name="Background agent runs"
                    stackId="llm-cost"
                    fill="#7c3aed"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                No captured LLM usage for this billing period.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>API/MCP Usage</CardTitle>
          <CardDescription>Organization-scoped request volume, failures, and recent logs for the selected time range.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-5">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(usageSummary?.summary.request_count || 0).toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">API + MCP calls</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Failures</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(usageSummary?.summary.failure_count || 0).toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">{formatPercent(usageSummary?.summary.failure_rate || 0)} failure rate</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Unique Credentials</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(usageSummary?.summary.unique_credentials || 0).toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">API keys/OAuth families</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{Math.round(usageSummary?.summary.avg_duration_ms || 0)}ms</div>
                <p className="text-xs text-muted-foreground">Recorded call timing</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Max Duration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(usageSummary?.summary.max_duration_ms || 0).toLocaleString()}ms</div>
                <p className="text-xs text-muted-foreground">Slowest call</p>
              </CardContent>
            </Card>
          </div>

          {usageLoading ? (
            <LoadingState label="Loading usage..." />
          ) : (
            <div className="grid gap-6 xl:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Usage Trend</CardTitle>
                  <CardDescription>API and MCP requests by period.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={usageTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" tickFormatter={formatPeriod} />
                      <YAxis />
                      <Tooltip labelFormatter={(value) => formatPeriod(String(value))} />
                      <Legend />
                      <Bar dataKey="api" name="API" fill="#2563eb" />
                      <Bar dataKey="mcp" name="MCP" fill="#16a34a" />
                      <Bar dataKey="failures" name="Failures" fill="#dc2626" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Top Operations</CardTitle>
                  <CardDescription>Highest-volume API routes and MCP tools.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Operation</TableHead>
                          <TableHead>Surface</TableHead>
                          <TableHead className="text-right">Requests</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(usageSummary?.top_operations || []).map((operation) => (
                          <TableRow key={`${operation.surface}:${operation.operation}`}>
                            <TableCell className="font-mono text-xs">{operation.operation}</TableCell>
                            <TableCell className="uppercase">{operation.surface}</TableCell>
                            <TableCell className="text-right">{operation.request_count.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                        {(usageSummary?.top_operations || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} className="h-24 text-center text-muted-foreground">No API/MCP usage found.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Surface</TableHead>
                  <TableHead>Operation</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(usageEvents?.items || []).map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="text-xs">{event.created_at ? new Date(event.created_at).toLocaleString() : '-'}</TableCell>
                    <TableCell className="uppercase">{event.surface}</TableCell>
                    <TableCell className="font-mono text-xs">{event.operation}</TableCell>
                    <TableCell>{event.status}{event.status_code ? ` ${event.status_code}` : ''}</TableCell>
                    <TableCell className="text-right">{event.duration_ms ?? '-'}ms</TableCell>
                  </TableRow>
                ))}
                {(usageEvents?.items || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">No recent usage events.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Organization Analytics</CardTitle>
              <CardDescription>Estimated model cost and conversation evaluation trend.</CardDescription>
            </div>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeRangeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Estimated Cost</CardTitle>
                <Database className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatUsd(analyticsSummary.totalCost)}</div>
                <p className="text-xs text-muted-foreground">Based on model pricing</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Cost / Conversation</CardTitle>
                <MessageCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  ${analyticsSummary.avgCostPerConversation.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                </div>
                <p className="text-xs text-muted-foreground">{analytics?.summary.llm_conversation_count || 0} LLM conversations</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tokens</CardTitle>
                <Database className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(analytics?.summary.total_tokens || 0).toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Input + output tokens</p>
              </CardContent>
            </Card>
          </div>

          {analyticsLoading ? (
            <LoadingState label="Loading analytics..." className="min-h-64" />
          ) : (
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Cost Trend</CardTitle>
                  <CardDescription>Estimated USD by period.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={costTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" tickFormatter={formatPeriod} />
                      <YAxis tickFormatter={(value) => `$${value}`} />
                      <Tooltip
                        formatter={(value) => formatUsd(Number(value || 0))}
                        labelFormatter={(value) => formatPeriod(String(value))}
                      />
                      <Bar dataKey="cost" name="Cost" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground">
        Last loaded {organization.updated_at ? formatDate(String(organization.updated_at)) : 'recently'}
      </div>
    </div>
  );
};

export default OrganizationDetailView;
